import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { THEME_BUCKETS } from "../lib/lessonThemes";

/**
 * Visual progression map: 12 consolidated themes covering all 200 lessons.
 * Each card is clickable and routes to /lessons?theme={slug}.
 * Numbers are real — they match the lessons.json data.
 */
const LEVEL_COLOR = {
  N5: { bg: "bg-[#FFD166]/15", border: "border-[#FFD166]", pill: "bg-[#FFD166] text-[#1A2A40]" },
  N4: { bg: "bg-[#E94A3F]/10", border: "border-[#E94A3F]/40", pill: "bg-[#E94A3F] text-white" },
  N3: { bg: "bg-[#1A2A40]/5", border: "border-[#1A2A40]/20", pill: "bg-[#1A2A40] text-white" },
};

export const ProgressionMap = () => {
  const total = THEME_BUCKETS.reduce((s, t) => s + t.count, 0);
  return (
    <section
      data-testid="progression-map"
      className="relative py-20 px-6 sm:px-12 bg-[#FDFBF7] border-y-2 border-[#1A2A40]/10"
    >
      <div className="max-w-6xl mx-auto">
        {/* Heading */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-white border border-[#1A2A40]/15 px-3 py-1 rounded-full text-xs font-bold tracking-widest text-[#E94A3F] mb-4">
            <span className="font-jp">日本語、一歩ずつ</span>
          </div>
          <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl text-[#1A2A40] leading-[1.05] tracking-tighter mb-4">
            La carte de progression.
            <br />
            <span className="italic font-light text-[#E94A3F]">200 leçons</span> structurées.
          </h2>
          <p className="text-base sm:text-lg text-[#4A5568] max-w-2xl mx-auto">
            12 grands thèmes vous emmènent du tout premier hiragana au keigo professionnel.
            Du <span className="font-bold">N5 au N3</span>, sans rien laisser au hasard.
            <span className="block mt-2 text-sm text-[#A0AEC0]">Cliquez sur un thème pour explorer ses leçons.</span>
          </p>
        </div>

        {/* Grid of themes */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {THEME_BUCKETS.map((t, i) => {
            const c = LEVEL_COLOR[t.level];
            return (
              <Link
                key={t.slug}
                to={`/lessons/theme/${t.slug}`}
                data-testid={`progression-theme-${t.slug}`}
                className={`group relative ${c.bg} ${c.border} border-2 rounded-2xl p-5 hover:scale-[1.03] hover:shadow-brut-sm transition-all`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="text-3xl">{t.emoji}</div>
                  <div className={`${c.pill} text-[10px] font-black tracking-widest px-2 py-1 rounded-full`}>
                    {t.level}
                  </div>
                </div>
                <div className="font-display font-black text-lg text-[#1A2A40] leading-tight mb-1">
                  {t.name}
                </div>
                <div className="text-xs text-[#4A5568] mb-3 leading-relaxed">{t.sub}</div>
                <div className="flex items-baseline justify-between">
                  <div className="flex items-baseline gap-1">
                    <span className="font-display font-black text-3xl text-[#E94A3F]">{t.count}</span>
                    <span className="text-xs text-[#A0AEC0] uppercase tracking-wider">leçons</span>
                  </div>
                  <ArrowRight className="w-4 h-4 text-[#1A2A40] opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                </div>
              </Link>
            );
          })}
        </div>

        {/* Footer total */}
        <div className="mt-12 flex flex-col sm:flex-row items-center justify-between gap-6 bg-white border-2 border-[#1A2A40] rounded-2xl p-6 shadow-brut-sm">
          <div>
            <div className="text-xs text-[#A0AEC0] uppercase tracking-widest mb-1">
              Total des leçons interactives
            </div>
            <div className="font-display font-black text-4xl text-[#1A2A40]">
              {total} leçons <span className="text-[#E94A3F]">·</span> 3 niveaux JLPT
            </div>
          </div>
          <Link
            to="/lessons"
            data-testid="progression-cta"
            className="bg-[#E94A3F] hover:bg-[#d83a2f] text-white font-bold py-3.5 px-6 rounded-xl shadow-brut-sm hover-lift inline-flex items-center gap-2 whitespace-nowrap"
          >
            Voir toutes les leçons <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ProgressionMap;
